import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useMemo,
  useCallback,
  useImperativeHandle,
  Fragment,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ROLES } from "../../utils/constants";
import classNames from "classnames";
import { notifyError, notifyInfo } from "../../utils/notify";
import { ORDER } from "../../components/Table/Table";
// style
import styles from "./PeriodScreen.module.scss";

import { ReactComponent as ChevronIcon } from "../../assets/icons/chevron_right.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/plus.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete2.svg";

import {
  AddButton,
  Chip,
  SearchInput,
  SelectInput,
  Input,
  Table,
  TableItem,
  CheckBox,
} from "../../components";

import {
  selectLoggedInUserRole,
  selectLoggedInUserDetails,
} from "../../store/slices/user/slice";

import { selectTeacherClasses } from "../../store/slices/class/slice";
import { fetchTeacherClasses } from "../../store/slices/class/asyncThunks";

import { selectStudents } from "../../store/slices/student/slice";
import debounce from "lodash.debounce";
import { fetchStudents } from "../../store/slices/student/asyncThunks";

import { createPeriod } from "../../store/slices/period/asyncThunks";

const PeriodScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // url period id
  const idPeriod = useParams();
  let page = +useLocation().search.split("=")[1];
  const ref = useRef();
  const [professor, setProfessor] = useState(0);
  const [periodName, setPeriodName] = useState("");

  const loggedInUserRole = useSelector(selectLoggedInUserRole);
  const loggedInUserDetails = useSelector(selectLoggedInUserDetails);
  const professors = useSelector(selectTeacherClasses);

  const { data, perPage, total } = useSelector(selectStudents);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortOptions, setSortOptions] = useState({
    sortBy: "",
    order: ORDER.ASC,
  });
  const [selectedStudent, setSelectedStudent] = useState(new Set());

  useEffect(() => {
    dispatch(fetchTeacherClasses(idPeriod.id));
  }, [dispatch, idPeriod]);

  const goBack = () => {
    navigate("/classes");
  };

  const setOption = (option) => {
    setProfessor(option.value);
  };

  const addStudent = (student) => {
    if (!selectedStudent.has(student.id)) {
      setSelectedStudent(
        (previousState) => new Set([...previousState, student.id])
      );
    } else {
      let newSet = new Set([...selectedStudent]);
      newSet.delete(student.id);
      setSelectedStudent(newSet);
    }
  };

  const selectAllStudents = () => {
    let newSet = new Set();
    if (data.length !== selectedStudent.size) {
      for (const st of data) {
        newSet.add(st.id);
      }
    }

    setSelectedStudent(newSet);
  };

  const tableHeader = [
    {
      key: "firstName",
      label: "Name",
      sortable: true,
    },
    {
      key: "externalId",
      label: "Student ID",
      sortable: true,
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
    },
    {
      key: "graduationYear",
      label: "Graduation Year",
      sortable: true,
    },
    {
      key: "",
      label: "",
    },
  ];

  const setPage = async (page) => {
    setCurrentPage(page);
    navigate({
      pathname: "",
      search: `?page=${page}`,
    });
  };

  const changeHandler = useCallback(
    (text) => {
      setSearch(text);
      dispatch(
        fetchStudents({
          page: Number(page),
          sortBy: sortOptions.sortBy,
          order: sortOptions.order,
          search: text,
        })
      );
    },
    [page, sortOptions, dispatch]
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler]
  );

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
    };
  });

  useEffect(() => {
    setCurrentPage(page);
    if (page > 0) {
      dispatch(
        fetchStudents({
          page: Number(page),
          sortBy: sortOptions.sortBy,
          order: sortOptions.order,
          search: search,
        })
      );
    }
    //eslint-disable-next-line
  }, [page, sortOptions, dispatch]);

  const addPeriod = () => {
    let result = {};

    result.name = periodName;
    result.teacherId = professor ? professor : professors[0].user.id;
    result.classId = idPeriod.id;
    result.students = [...selectedStudent];

    const occurs = ref.current.getData();

    result.periodTimes = occurs.periodTimes;
    result.from = occurs.from;
    result.to = occurs.to;

    if (
      result.from.length === 0 ||
      result.to.length === 0 ||
      result.to <= result.from
    ) {
      notifyError("Date from must be before date to");
      return;
    }

    dispatch(createPeriod(result)).then((promise) => {
      if (promise.type.includes("fulfilled")) {
        notifyInfo("Successfully deleted period");
        navigate("/classes");
      } else {
        notifyError(promise.error.message);
      }
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.backContainer} onClick={() => goBack()}>
          <div className={styles.iconBack}>
            <ChevronIcon />
          </div>
          <span>Return to classes</span>
        </div>
        <div className={styles.profesorContainer}>
          <h1>New period</h1>
          <AddButton onClick={addPeriod} title="Create period" />
        </div>
      </div>
      <div>
        <div className={styles.profesorContainer}>
          {loggedInUserRole === ROLES.ADMIN &&
            professors &&
            professors.length > 0 && (
              <div className={styles.profesorAndPeriodName}>
                <span className={styles.constProfessorLabelAdmin}>
                  Professor
                </span>
                <div className={styles.row}>
                  <SelectInput
                    className={styles.selectTeacher}
                    id="selectTeacher"
                    label="Professor"
                    options={professors.map((item, index) => {
                      return {
                        name: `${item.user.firstName} ${item.user.lastName}`,
                        value: item.user.id,
                      };
                    })}
                    setOption={setOption}
                  />

                  <Input
                    placeholder="Period name"
                    onChange={(event) => setPeriodName(event)}
                    value={periodName || ""}
                    className={styles.inputPeriodName}
                  />
                </div>
              </div>
            )}
          {loggedInUserRole === ROLES.TEACHER && (
            <div className={styles.profesorAndPeriodName}>
              <div className={styles.profesorLabel}>
                <div className={styles.profesorLabelText}>
                  <span className={styles.constProfessorLabelTeacher}>
                    Professor
                  </span>
                </div>
                <div className={styles.profesorRole}>
                  <Chip {...loggedInUserDetails} />
                  <span className={styles.teacherFullName}>
                    {loggedInUserDetails.firstName}{" "}
                    {loggedInUserDetails.lastName}
                  </span>
                </div>
                <div className={styles.profesorLabel}>
                  <div>
                    <Input
                      placeholder="Period name"
                      onChange={(event) => setPeriodName(event)}
                      value={periodName || ""}
                      className={styles.inputPeriodName}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DateRangeOccur ref={ref} />
      </div>
      <div>
        <div className={styles.header}>
          <SearchInput
            onChange={debouncedChangeHandler}
            placeholder="Search Students"
          />

          <AddButton onClick={selectAllStudents} title="Select All" />
        </div>
        <div className={styles.content}>
          <Table
            currentPage={currentPage}
            totalCount={total}
            pageSize={perPage}
            setPage={setPage}
            noDataContent="No Data"
            setSortOptions={setSortOptions}
            sortOptions={sortOptions}
            headerInformation={tableHeader}
          >
            {data.map((student, index) => {
              return (
                <Fragment key={index}>
                  <TableItem
                    data={
                      <div className={styles.nameContainer}>
                        <Chip
                          img={student.profilePhoto}
                          firstName={student.firstName}
                          lastName={student.lastName}
                        />
                        <p className={styles.nameText}>
                          {student.firstName + " " + student.lastName}
                        </p>
                      </div>
                    }
                  />
                  <TableItem data={student.externalId} />
                  <TableItem data={student.email} />
                  <TableItem data={student.graduationYear} />
                  <TableItem
                    data={
                      <div className={styles.iconContainer}>
                        <CheckBox
                          onClick={() => {
                            addStudent(student);
                          }}
                          className={styles.icon}
                          checked={selectedStudent.has(student.id)}
                        />
                      </div>
                    }
                  />
                </Fragment>
              );
            })}
          </Table>
        </div>
      </div>
    </div>
  );
};

const DateRangeOccur = forwardRef((props, ref) => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const initialState = {
    hoursFrom: "07",
    hoursTo: "07",
    minutesFrom: "00",
    minutesTo: "00",
    days: [],
  };

  const [occurs, setOccurs] = useState([initialState]);

  useImperativeHandle(ref, () => ({
    getData() {
      let data = [...occurs];

      let result = {};

      var resultJson = [];
      for (const ocur of data) {
        let ocurJson = {};
        if (ocur.days.length > 1) {
          for (const day of ocur.days) {
            ocurJson = {};
            ocurJson.from = ocur.hoursFrom + ":" + ocur.minutesFrom + ":00";
            ocurJson.to = ocur.hoursTo + ":" + ocur.minutesTo + ":00";
            ocurJson.day = day;
            if (ocurJson.from >= ocurJson.to) {
              notifyError("Time from must be before time to");
              return;
            }
            resultJson.push(ocurJson);
          }
        } else if (ocur.days.length === 1) {
          ocurJson.from = ocur.hoursFrom + ":" + ocur.minutesFrom + ":00";
          ocurJson.to = ocur.hoursTo + ":" + ocur.minutesTo + ":00";
          ocurJson.day = ocur.days[0];
          resultJson.push(ocurJson);
          if (ocurJson.from >= ocurJson.to) {
            notifyError("Time from must be before time to");
            return;
          }
        } else {
          notifyError("Please select day of period");
          return;
        }
      }
      result.periodTimes = resultJson;
      result.from = dateFrom;
      result.to = dateTo;

      return result;
    },
  }));

  const handleChangeRange = (e, option) => {
    if (option === "from") {
      setDateFrom(e.target.value);
    } else {
      setDateTo(e.target.value);
    }
  };

  const dayChanged = (day, index, e) => {
    let data = [...occurs];
    let ocur = data[index];
    data.splice(index, 1);

    if (e.target.checked) {
      if (!ocur.days.includes(day)) {
        ocur.days.push(day);
      }
    } else {
      if (ocur.days.includes(day)) {
        let index = ocur.days.indexOf(day);
        ocur.days.splice(index, 1);
      }
    }
    data.splice(index, 0, ocur);
    setOccurs([...data]);
  };

  const handleDeleteTime = (index) => {
    const data = [...occurs];
    data.splice(index, 1);
    setOccurs(data);
  };

  const handleAddTime = () => {
    setOccurs([...occurs, initialState]);
  };

  const setTime = (index, e, option) => {
    const tmpVal = [...occurs];
    tmpVal[index][option] = e.target.value;
    setOccurs(tmpVal);
  };

  return (
    <div className={styles.datesRowFirstContainer}>
      <div className={styles.datesColumn}>
        <h2 className={styles.h2Label}>Dates</h2>

        <div className={styles.datesRow}>
          <div className={styles.datesFromTo}>
            <input
              type="date"
              id="from"
              onChange={(date) => handleChangeRange(date, "from")}
              value={dateFrom}
            />
            <span className={styles.dividerTo}>to</span>
            <input
              type="date"
              id="to"
              onChange={(date) => handleChangeRange(date, "to")}
              value={dateTo}
            />
          </div>
          <div className={styles.timesFromTo}>
            {occurs.map((i, index) => (
              <div key={index}>
                <select
                  name="hoursFrom"
                  id="hoursFrom"
                  onChange={(data) => setTime(index, data, "hoursFrom")}
                >
                  <option value="07">07</option>
                  <option value="08">08</option>
                  <option value="09">09</option>
                  <option value="10">10</option>
                  <option value="11">11</option>
                  <option value="12">12</option>
                  <option value="13">13</option>
                  <option value="14">14</option>
                  <option value="15">15</option>
                  <option value="16">16</option>
                  <option value="17">17</option>
                  <option value="18">18</option>
                  <option value="19">19</option>
                </select>
                <span>:</span>
                <select
                  name="minutesFrom"
                  id="minutesFrom"
                  onChange={(data) => setTime(index, data, "minutesFrom")}
                >
                  <option value="00">00</option>
                  <option value="05">05</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="15">20</option>
                  <option value="15">25</option>
                  <option value="30">30</option>
                  <option value="15">35</option>
                  <option value="15">40</option>
                  <option value="45">45</option>
                  <option value="15">50</option>
                  <option value="15">55</option>
                </select>
                <span className={styles.dividerTo}>to</span>
                <select
                  name="hoursTo"
                  id="hoursTo"
                  onChange={(data) => setTime(index, data, "hoursTo")}
                >
                  <option value="07">07</option>
                  <option value="08">08</option>
                  <option value="09">09</option>
                  <option value="10">10</option>
                  <option value="11">11</option>
                  <option value="12">12</option>
                  <option value="13">13</option>
                  <option value="14">14</option>
                  <option value="15">15</option>
                  <option value="16">16</option>
                  <option value="17">17</option>
                  <option value="18">18</option>
                  <option value="19">19</option>
                </select>
                <span>:</span>
                <select
                  name="minutesTo"
                  id="minutesTo"
                  onChange={(data) => setTime(index, data, "minutesTo")}
                >
                  <option value="00">00</option>
                  <option value="05">05</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="15">20</option>
                  <option value="15">25</option>
                  <option value="30">30</option>
                  <option value="15">35</option>
                  <option value="15">40</option>
                  <option value="45">45</option>
                  <option value="15">50</option>
                  <option value="15">55</option>
                </select>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.datesColumn}>
        <h2 className={styles.h2Label}>Repeat on</h2>
        <div className={styles.datesRow}>
          {occurs.map((item, index) => (
            <div className={styles.daysOccurs} key={index}>
              <input
                type="checkbox"
                name="dayOfWeek"
                className={styles.dayOfWeek}
                id={`Sunday_${index}`}
                onChange={(e) => dayChanged(0, index, e)}
              />
              <label htmlFor={`Sunday_${index}`}>S</label>
              <input
                type="checkbox"
                name="dayOfWeek"
                className={styles.dayOfWeek}
                id={`Monday_${index}`}
                onChange={(e) => dayChanged(1, index, e)}
              />
              <label htmlFor={`Monday_${index}`}>M</label>
              <input
                type="checkbox"
                name="dayOfWeek"
                className={styles.dayOfWeek}
                id={`Tuesday_${index}`}
                onChange={(e) => dayChanged(2, index, e)}
              />{" "}
              <label htmlFor={`Tuesday_${index}`}>T</label>
              <input
                type="checkbox"
                name="dayOfWeek"
                className={styles.dayOfWeek}
                id={`Wednesday_${index}`}
                onChange={(e) => dayChanged(3, index, e)}
              />{" "}
              <label htmlFor={`Wednesday_${index}`}>W</label>
              <input
                type="checkbox"
                name="dayOfWeek"
                className={styles.dayOfWeek}
                id={`Thursday_${index}`}
                onChange={(e) => dayChanged(4, index, e)}
              />{" "}
              <label htmlFor={`Thursday_${index}`}>T</label>
              <input
                type="checkbox"
                name="dayOfWeek"
                className={styles.dayOfWeek}
                id={`Friday_${index}`}
                onChange={(e) => dayChanged(5, index, e)}
              />{" "}
              <label htmlFor={`Friday_${index}`}>F</label>
              <input
                type="checkbox"
                name="dayOfWeek"
                className={styles.dayOfWeek}
                id={`Saturday_${index}`}
                onChange={(e) => dayChanged(6, index, e)}
              />{" "}
              <label htmlFor={`Saturday_${index}`}>S</label>
              {occurs.length === index + 1 && (
                <div className={styles.actionButtons}>
                  <div
                    className={classNames(styles.actionBtn, styles.add)}
                    onClick={handleAddTime}
                  >
                    <PlusIcon className={styles.icon} />
                  </div>

                  {index > 0 && (
                    <div
                      className={classNames(styles.delete, styles.actionBtn)}
                      onClick={() => handleDeleteTime(index)}
                    >
                      <DeleteIcon className={styles.icon} />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export default PeriodScreen;
